import { useState, useContext } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { selectData } from 'layouts/onboarding'
import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
  subMonths,
} from 'date-fns'
function createData(name, working, retirement) {
  return { name, working, retirement }
}
export const useBuyoutCalculations = (deductions = [], liabilities = []) => {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { intakeState } = useContext(IntakeContext)

    const personalDetailsCurrentAge =
      differenceInMonths(new Date(), new Date(intakeState.dateOfBirth)) / 12
    // Mandatory Retirement Age
    const mandatoryRetirementAge =
      intakeState?.retirementSystem === 'special_fers_(leo,_atc_etc.)'
        ? 57
        : intakeState?.retirementSystem ===
          'foreign_service_officer_pension_(fsps)'
        ? 65
        : ''

    const birthYear = new Date(intakeState?.dateOfBirth).getFullYear()

    const minimumRetirementAge =
      birthYear < 1948
        ? 55
        : birthYear === 1948
        ? 55.1667
        : birthYear === 1949
        ? 55.333
        : birthYear === 1950
        ? 55.5
        : birthYear === 1951
        ? 55.667
        : birthYear === 1952
        ? 55.833
        : birthYear >= 1953 && birthYear <= 1964
        ? 56
        : birthYear === 1965
        ? 56.1667
        : birthYear === 1966
        ? 56.333
        : birthYear === 1967
        ? 56.5
        : birthYear === 1968
        ? 56.667
        : birthYear === 1969
        ? 56.833
        : 57
    // console.log(minimumRetirementAge)

    //Eligibility Scenarios

    const eligibilityScenariosFirstFullyEligibleDate = min([
      max([
        addYears(new Date(intakeState.dateOfHire), 30),
        addYears(new Date(intakeState.dateOfBirth), 57),
      ]),
      max([
        addYears(new Date(intakeState.dateOfHire), 20),
        addYears(new Date(intakeState.dateOfBirth), 60),
      ]),
      max([
        addYears(new Date(intakeState.dateOfHire), 5),
        addYears(new Date(intakeState.dateOfBirth), 62),
      ]),
    ])
    const eligibilityScenariosFirstFullyEligibleDateFSPS = min([
      max([
        addYears(new Date(intakeState.dateOfHire), 20),
        addYears(new Date(intakeState.dateOfBirth), 50),
      ]),
      max([
        addYears(new Date(intakeState.dateOfHire), 5),
        addYears(new Date(intakeState.dateOfBirth), 62),
      ]),
    ])

    const eligibilityScenariosFirstFullyEligibleAge =
      differenceInMonths(
        min([
          max([
            addYears(new Date(intakeState.dateOfHire), 30),
            addYears(new Date(intakeState.dateOfBirth), 57),
          ]),
          max([
            addYears(new Date(intakeState.dateOfHire), 20),
            addYears(new Date(intakeState.dateOfBirth), 60),
          ]),
          max([
            addYears(new Date(intakeState.dateOfHire), 5),
            addYears(new Date(intakeState.dateOfBirth), 62),
          ]),
        ]),
        new Date(intakeState.dateOfBirth)
      ) / 12
    const eligibilityScenariosModeledRetirementAgeSub =
      differenceInMonths(
        max([
          new Date(),
          min([
            max([
              addYears(new Date(intakeState.dateOfHire), 30),
              addYears(new Date(intakeState.dateOfBirth), 57),
            ]),
            max([
              addYears(new Date(intakeState.dateOfHire), 20),
              addYears(new Date(intakeState.dateOfBirth), 60),
            ]),
            max([
              addYears(new Date(intakeState.dateOfHire), 5),
              addYears(new Date(intakeState.dateOfBirth), 62),
            ]),
          ]),
        ]),
        new Date(intakeState.dateOfBirth)
      ) / 12

    const ageOnSept30th2025 =
      differenceInMonths(
        new Date(2025, 9, 30),
        new Date(intakeState.dateOfBirth)
      ) / 12

    const cysOnSept30th2025 =
      differenceInMonths(
        new Date(2025, 9, 30),
        new Date(intakeState.dateOfHire)
      ) / 12

    const fullyEligibleRetirementBeforeSept30first =
      ageOnSept30th2025 >= 62 &&
      intakeState?.retirementSystem !==
        'foreign_service_officer_pension_(fsps)' &&
      cysOnSept30th2025 >= 5

    const fullyEligibleRetirementBeforeSept30second =
      ageOnSept30th2025 >= 60 &&
      intakeState?.retirementSystem !==
        'foreign_service_officer_pension_(fsps)' &&
      cysOnSept30th2025 >= 20

    const fullyEligibleRetirementBeforeSept30third =
      ageOnSept30th2025 >= minimumRetirementAge &&
      intakeState?.retirementSystem !==
        'foreign_service_officer_pension_(fsps)' &&
      cysOnSept30th2025 >= 30

    const mraPlus10Sept30 =
      ageOnSept30th2025 >= minimumRetirementAge &&
      intakeState?.retirementSystem !==
        'foreign_service_officer_pension_(fsps)' &&
      cysOnSept30th2025 >= 10

    const earlyRetirementSept30 =
      ageOnSept30th2025 >= minimumRetirementAge &&
      intakeState?.retirementSystem !==
        'foreign_service_officer_pension_(fsps)' &&
      cysOnSept30th2025 >= 10

    const notEligibleSept30 = cysOnSept30th2025 < 5

    const deferredRetirementSept30first =
      ageOnSept30th2025 < 62 &&
      intakeState?.retirementSystem === 'fers' &&
      cysOnSept30th2025 > 5 &&
      cysOnSept30th2025 < 10

    const deferredRetirementSept30second =
      ageOnSept30th2025 < 60 &&
      intakeState?.retirementSystem === 'fers' &&
      cysOnSept30th2025 > 5 &&
      cysOnSept30th2025 < 10

    const deferredRetirementSept30third =
      ageOnSept30th2025 < minimumRetirementAge &&
      intakeState?.retirementSystem === 'fers' &&
      cysOnSept30th2025 > 5 &&
      cysOnSept30th2025 < 30

    const VERAfirst =
      ageOnSept30th2025 >= 50 &&
      intakeState?.retirementSystem !==
        'foreign_service_officer_pension_(fsps)' &&
      cysOnSept30th2025 >= 20

    const VERAsecond =
      intakeState?.retirementSystem !==
        'foreign_service_officer_pension_(fsps)' && cysOnSept30th2025 >= 25

    return {
      personalDetailsCurrentAge,
      minimumRetirementAge,
      eligibilityScenariosFirstFullyEligibleDate,
      eligibilityScenariosModeledRetirementAgeSub,
      ageOnSept30th2025,
      cysOnSept30th2025,
      fullyEligibleRetirementBeforeSept30first,
      fullyEligibleRetirementBeforeSept30second,
      fullyEligibleRetirementBeforeSept30third,
      mraPlus10Sept30,
      earlyRetirementSept30,
      notEligibleSept30,
      deferredRetirementSept30first,
      deferredRetirementSept30second,
      deferredRetirementSept30third,
      VERAfirst,
      VERAsecond,
    }
  } catch (e) {
    console.log(e)
    return {}
  }
}
